const whyNeutrals = [
  {
    icon: "ri-moon-line",
    title: "Better Sleep",
    body: "Neutral tones reduce visual stimulation, helping your nervous system wind down faster and stay calm through the night.",
  },
  {
    icon: "ri-sun-line",
    title: "Timeless Appeal",
    body: "A warm neutral bedroom never goes out of style. It's a foundation you can layer and evolve without ever starting over.",
  },
  {
    icon: "ri-leaf-line",
    title: "Natural Harmony",
    body: "Creams, oats, and warm whites mirror the tones found in nature — linen, stone, sand — creating an instinctively restful environment.",
  },
];

const palette = [
  { name: "Warm White", hex: "#F5F0E8", role: "Walls & ceiling — the base that makes everything feel open" },
  { name: "Oat Linen", hex: "#E8DDD0", role: "Bedding & soft furnishings — the primary texture layer" },
  { name: "Warm Taupe", hex: "#C4B5A5", role: "Accents & trim — adds depth without contrast" },
  { name: "Natural Oak", hex: "#B8956A", role: "Wood tones — furniture and flooring warmth" },
  { name: "Soft Clay", hex: "#9E8070", role: "Cushions & throws — the grounding accent" },
];

const furnitureTips = [
  {
    title: "Low-Profile Bed Frame",
    desc: "A low platform or slatted bed frame in natural oak or walnut keeps the room feeling open and grounded. Avoid heavy headboards that dominate the space.",
    image: "https://public.readdy.ai/ai/img_res/2f047d26f380bf3fc3c0e4e5375830b0.jpg",
  },
  {
    title: "Linen Bedding",
    desc: "Washed linen in oat, ivory, or warm white is the single most impactful change you can make. It adds texture, breathes well, and looks effortlessly calm.",
    image: "https://readdy.ai/api/search-image?query=washed%20linen%20bedding%20oat%20ivory%20warm%20white%20bedroom%20soft%20morning%20light%20rumpled%20natural%20texture%20calm%20elegant%20editorial%20interior%20photography%20neutral%20palette&width=600&height=480&seq=cbn_furn2&orientation=landscape",
  },
  {
    title: "Nightstands with Warmth",
    desc: "Choose nightstands in natural wood or rattan. Avoid cold metals or high-gloss finishes — they break the warmth of a neutral palette.",
    image: "https://readdy.ai/api/search-image?query=natural%20rattan%20nightstand%20warm%20bedroom%20soft%20lamp%20neutral%20palette%20calm%20elegant%20editorial%20interior%20photography%20warm%20ivory%20oat%20tones&width=600&height=480&seq=cbn_furn3&orientation=landscape",
  },
  {
    title: "Soft Area Rug",
    desc: "A wool or cotton rug in warm ivory or oat anchors the bed and adds a layer of softness underfoot. Keep it simple — no bold patterns.",
    image: "https://readdy.ai/api/search-image?query=soft%20wool%20area%20rug%20warm%20ivory%20oat%20bedroom%20natural%20light%20calm%20minimal%20elegant%20editorial%20interior%20photography%20neutral%20warm%20palette&width=600&height=480&seq=cbn_furn4&orientation=landscape",
  },
  {
    title: "Sheer Linen Curtains",
    desc: "Floor-to-ceiling sheer linen curtains filter morning light beautifully and make the ceiling feel higher. Hang them wide and high for maximum effect.",
    image: "https://readdy.ai/api/search-image?query=sheer%20linen%20curtains%20floor%20to%20ceiling%20bedroom%20warm%20morning%20light%20neutral%20palette%20calm%20elegant%20editorial%20interior%20photography%20ivory%20cream&width=600&height=480&seq=cbn_furn5&orientation=landscape",
  },
  {
    title: "Minimal Lighting",
    desc: "A simple ceramic or linen table lamp on each nightstand creates warm, low-level light for evenings. Avoid overhead lighting as the primary source.",
    image: "https://readdy.ai/api/search-image?query=ceramic%20table%20lamp%20warm%20light%20neutral%20bedroom%20nightstand%20calm%20minimal%20elegant%20editorial%20interior%20photography%20warm%20ivory%20palette%20evening%20glow&width=600&height=480&seq=cbn_furn6&orientation=landscape",
  },
];

const stylingTips = [
  "Layer your bedding — start with a fitted sheet, add a flat sheet, then a linen duvet, then a throw folded at the foot.",
  "Keep the nightstand to three items maximum: lamp, one book, one small object.",
  "Use a single large piece of art rather than a gallery wall — it feels calmer and more considered.",
  "Add a small plant or dried botanicals for a natural, organic element without colour.",
  "Keep the floor as clear as possible — visual clutter on the floor makes a room feel smaller and more anxious.",
  "Choose one warm wood tone and repeat it across furniture pieces for cohesion.",
  "Use blackout linen curtains behind sheers for a layered look that also blocks light when needed.",
];

export default function CBNContent() {
  return (
    <article className="bg-white">
      {/* Why Neutrals */}
      <section className="py-24 max-w-screen-xl mx-auto px-8 md:px-16">
        <div className="max-w-2xl mx-auto text-center mb-16">
          <p className="text-[#B5A898] text-[10px] tracking-[0.45em] uppercase mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>
            Why It Works
          </p>
          <h2 className="text-[#2C2825] text-3xl md:text-4xl mb-5" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}>
            The Case for a Neutral Bedroom
          </h2>
          <p className="text-[#8B8378] text-sm leading-relaxed" style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}>
            Colour has a measurable effect on mood and sleep quality. A warm neutral palette isn't just aesthetically pleasing — it's physiologically calming.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {whyNeutrals.map((item) => (
            <div key={item.title} className="text-center px-4">
              <div className="w-12 h-12 flex items-center justify-center mx-auto mb-5 rounded-full bg-[#F5F1E8]">
                <i className={`${item.icon} text-[#9E8070] text-xl`} />
              </div>
              <h3 className="text-[#2C2825] text-lg mb-3" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}>
                {item.title}
              </h3>
              <p className="text-[#8B8378] text-sm leading-relaxed" style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}>
                {item.body}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Palette */}
      <section className="py-20 bg-[#FAF7F2]">
        <div className="max-w-screen-xl mx-auto px-8 md:px-16">
          <div className="max-w-2xl mx-auto text-center mb-14">
            <p className="text-[#B5A898] text-[10px] tracking-[0.45em] uppercase mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>
              The Palette
            </p>
            <h2 className="text-[#2C2825] text-3xl md:text-4xl mb-4" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}>
              Build Your Colour Story
            </h2>
            <p className="text-[#8B8378] text-sm leading-relaxed" style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}>
              Five tones that work together to create a bedroom that feels warm, cohesive, and deeply restful.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {palette.map((swatch) => (
              <div key={swatch.name} className="flex-1 max-w-[200px] mx-auto sm:mx-0">
                <div
                  className="w-full h-28 rounded-xl mb-4"
                  style={{ backgroundColor: swatch.hex }}
                />
                <p className="text-[#2C2825] text-sm font-medium mb-1" style={{ fontFamily: "'Inter', sans-serif" }}>
                  {swatch.name}
                </p>
                <p className="text-[#B5A898] text-[10px] tracking-[0.1em] mb-2" style={{ fontFamily: "'Inter', sans-serif" }}>
                  {swatch.hex}
                </p>
                <p className="text-[#8B8378] text-xs leading-relaxed" style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}>
                  {swatch.role}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Furniture Tips */}
      <section className="py-24 max-w-screen-xl mx-auto px-8 md:px-16">
        <div className="max-w-2xl mx-auto text-center mb-16">
          <p className="text-[#B5A898] text-[10px] tracking-[0.45em] uppercase mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>
            Key Pieces
          </p>
          <h2 className="text-[#2C2825] text-3xl md:text-4xl mb-4" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}>
            Furniture & Textiles That Work
          </h2>
          <p className="text-[#8B8378] text-sm leading-relaxed" style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}>
            Every piece in a calm bedroom should earn its place. Here's what to choose and why.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {furnitureTips.map((tip) => (
            <div key={tip.title} className="group">
              <div className="w-full h-52 rounded-xl overflow-hidden bg-[#EDE6DA] mb-5">
                <img
                  src={tip.image}
                  alt={tip.title}
                  className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                />
              </div>
              <h3 className="text-[#2C2825] text-lg mb-2" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}>
                {tip.title}
              </h3>
              <p className="text-[#8B8378] text-sm leading-relaxed" style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}>
                {tip.desc}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Styling Tips */}
      <section className="py-20 bg-[#F5F1E8]">
        <div className="max-w-screen-xl mx-auto px-8 md:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-[#B5A898] text-[10px] tracking-[0.45em] uppercase mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>
                Styling Tips
              </p>
              <h2 className="text-[#2C2825] text-3xl md:text-4xl mb-8" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}>
                7 Rules for a Calmer Bedroom
              </h2>
              <ul className="space-y-5">
                {stylingTips.map((tip, i) => (
                  <li key={i} className="flex items-start gap-4">
                    <span
                      className="text-[#C4B5A5] text-xs mt-0.5 shrink-0 w-5"
                      style={{ fontFamily: "'Inter', sans-serif" }}
                    >
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <p className="text-[#5C5248] text-sm leading-relaxed" style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}>
                      {tip}
                    </p>
                  </li>
                ))}
              </ul>
            </div>
            <div className="w-full h-[520px] rounded-2xl overflow-hidden bg-[#EDE6DA]">
              <img
                src="https://readdy.ai/api/search-image?query=beautifully%20styled%20calm%20neutral%20bedroom%20warm%20ivory%20linen%20bedding%20natural%20oak%20furniture%20soft%20morning%20light%20sheer%20curtains%20minimal%20elegant%20editorial%20interior%20photography%20serene%20atmosphere&width=800&height=1040&seq=cbn_tips_img&orientation=portrait"
                alt="Calm neutral bedroom styling"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>
    </article>
  );
}
